import { useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { getLocalTimeZone } from "@internationalized/date";

import FormFieldCronExpressions from "components/FormFields/CronExpressionsField";
import FormFieldTimezone from "components/FormFields/Timezone";
import Box from "ds/components/Box";
import Datepicker from "ds/components/Datepicker";
import FormField from "ds/components/Form/Field";
import SegmentedControl from "ds/components/SegmentedControl";
import Tile from "ds/components/Tile";
import { TimezoneDisplay } from "shared/Stack/Scheduling/types";
import { getNowDateTime } from "utils/date";
import { validateDateIsAfterNow } from "utils/formValidators";
import Card from "ds/components/Card";

type ScheduleSelectorProps = {
  minDate?: Date;
  recurringTitle?: string;
  recurringDescription: string;
  specificTimeTitle?: string;
  specificTimeDescription: string;
};

type TFieldValues = {
  recurring: boolean;
  timezoneDisplay?: TimezoneDisplay;
  dateTime: Date;
  cronSchedule: string[];
  timezone: string;
};

const ScheduleSelector = ({
  minDate,
  recurringTitle = "Recurring",
  recurringDescription,
  specificTimeTitle = "Specific time",
  specificTimeDescription,
}: ScheduleSelectorProps) => {
  const { control, watch, setValue } = useFormContext<TFieldValues>();

  const isRecurring = watch("recurring");
  const timezoneDisplay = watch("timezoneDisplay");

  const localTimezone = getLocalTimeZone();

  const timezoneSegments = useMemo(
    () => [
      { value: "local", label: "Local timezone" },
      { value: "utc", label: "UTC" },
    ],
    []
  );

  const dynamicMinDate = useMemo(() => {
    if (!minDate) return undefined;

    return getNowDateTime({
      timezone: timezoneDisplay === "local" ? localTimezone : "UTC",
    });
  }, [minDate, timezoneDisplay, localTimezone]);

  const handleRecurringChange = useCallback(
    (recurring: boolean) => {
      setValue("recurring", recurring, { shouldValidate: true, shouldDirty: true });
    },
    [setValue]
  );

  return (
    <Card variant="secondary" contentGap="x-large">
      <Box gap="x-large">
        <Tile
          title={recurringTitle}
          description={recurringDescription}
          indicator="radio"
          selected={isRecurring}
          onClick={() => handleRecurringChange(true)}
        />
        <Tile
          title={specificTimeTitle}
          description={specificTimeDescription}
          indicator="radio"
          selected={!isRecurring}
          onClick={() => handleRecurringChange(false)}
        />
      </Box>

      {isRecurring && (
        <>
          <FormFieldCronExpressions name="cronSchedule" />
          <FormFieldTimezone name="timezone" />
        </>
      )}

      {!isRecurring && (
        <>
          {timezoneDisplay !== undefined && (
            <Box direction="column" gap="large" padding="medium 0 0" fullWidth>
              <Controller
                name="timezoneDisplay"
                control={control}
                render={({ field }) => (
                  <SegmentedControl
                    value={field.value}
                    options={timezoneSegments}
                    onChange={field.onChange}
                  />
                )}
              />
            </Box>
          )}

          <Controller
            name="dateTime"
            control={control}
            rules={{
              required: true,
              validate: validateDateIsAfterNow,
            }}
            render={({ field, fieldState }) => (
              <FormField label="Date and time" error={fieldState.error?.message} noMargin>
                <Datepicker
                  withTime
                  renderTimezone={timezoneDisplay === "local" ? localTimezone : "UTC"}
                  date={field.value}
                  onChange={field.onChange}
                  minDate={dynamicMinDate || minDate}
                  hasError={!!fieldState.error?.message}
                  fullWidth
                />
              </FormField>
            )}
          />
        </>
      )}
    </Card>
  );
};

export default ScheduleSelector;
